import apiService from './ApiService';
import ParticularService from './ParticularService';

const getServerDownloadMessage = async (response, fallbackMessage) => {
  let rawText = '';
  try {
    rawText = await response.clone().text();
  } catch (_) {
    rawText = '';
  }

  const trimmedText = rawText.trim();
  if (trimmedText) {
    try {
      const payload = JSON.parse(trimmedText);
      const keys = ['mensaje', 'Mensaje', 'message', 'Message', 'error', 'Error', 'detail', 'Detail', 'title', 'Title'];
      for (const key of keys) {
        if (payload?.[key]) return payload[key];
      }
    } catch (_) {
      return trimmedText;
    }
  }

  return fallbackMessage || `HTTP Error: ${response.status} ${response.statusText || ''}`.trim();
};

class ParticularesApiService {
  async getContexto() {
    const response = await apiService.get('/particulares/contexto');
    const particular = response?.particular || response?.Particular || null;
    if (particular) {
      ParticularService.syncActiveParticular(particular);
    }
    return response;
  }

  getDashboard() {
    return apiService.get('/particulares/dashboard');
  }

  getMensajeDestinatarios() {
    return apiService.get('/particulares/mensajes/destinatarios');
  }

  createMensaje(payload) {
    return apiService.post('/particulares/mensajes', payload);
  }

  getMensajes({ skip = 0, take = 50, search = '', leido = 'todos' } = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(skip)) params.append('skip', skip);
    if (Number.isFinite(take)) params.append('take', take);
    if (search) params.append('search', search);
    if (leido) params.append('leido', leido);
    const query = params.toString();
    return apiService.get(`/particulares/mensajes${query ? `?${query}` : ''}`);
  }

  getMensajesEnviados({ skip = 0, take = 50, search = '' } = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(skip)) params.append('skip', skip);
    if (Number.isFinite(take)) params.append('take', take);
    if (search) params.append('search', search);
    const query = params.toString();
    return apiService.get(`/particulares/mensajes/enviados${query ? `?${query}` : ''}`);
  }

  getMensaje(destinatarioId) {
    if (!destinatarioId) throw new Error('Mensaje inválido.');
    return apiService.get(`/particulares/mensajes/${encodeURIComponent(destinatarioId)}`);
  }

  getMensajeEnviado(mensajeId) {
    if (!mensajeId) throw new Error('Mensaje inválido.');
    return apiService.get(`/particulares/mensajes/enviados/${encodeURIComponent(mensajeId)}`);
  }

  updateMensajeLeido(destinatarioId, leido) {
    if (!destinatarioId) throw new Error('Mensaje inválido.');
    return apiService.put(`/particulares/mensajes/${encodeURIComponent(destinatarioId)}/leido`, {
      leido: Boolean(leido)
    });
  }

  getMensajesNoLeidosCount() {
    return apiService.get('/particulares/mensajes/no-leidos/count');
  }

  getEtiquetas() {
    return apiService.get('/particulares/etiquetas');
  }

  createEtiqueta(payload) {
    return apiService.post('/particulares/etiquetas', payload);
  }

  updateEtiqueta(id, payload) {
    return apiService.put(`/particulares/etiquetas/${id}`, payload);
  }

  deleteEtiqueta(id) {
    return apiService.delete(`/particulares/etiquetas/${id}`);
  }

  getProfesores() {
    return apiService.get('/particulares/profesores');
  }

  getDisponibilidades({ from = '', to = '', profesorId = '' } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    if (profesorId) params.append('profesorId', profesorId);
    const query = params.toString();
    return apiService.get(`/particulares/disponibilidades${query ? `?${query}` : ''}`);
  }

  getDisponibilidadAlumnos({ profesorId = '' } = {}) {
    const params = new URLSearchParams();
    if (profesorId) params.append('profesorId', profesorId);
    const query = params.toString();
    return apiService.get(`/particulares/disponibilidades/alumnos${query ? `?${query}` : ''}`);
  }

  createDisponibilidad(payload) {
    return apiService.post('/particulares/disponibilidades', payload);
  }

  updateDisponibilidad(id, payload) {
    return apiService.put(`/particulares/disponibilidades/${encodeURIComponent(id)}`, payload);
  }

  deleteDisponibilidad(id) {
    return apiService.delete(`/particulares/disponibilidades/${encodeURIComponent(id)}`);
  }

  getProfesoresAsociados() {
    return apiService.get('/particulares/profesores-asociados');
  }

  createProfesorAsociado(payload) {
    return apiService.post('/particulares/profesores-asociados', payload);
  }

  updateProfesorAsociado(id, payload) {
    return apiService.put(`/particulares/profesores-asociados/${id}`, payload);
  }

  updateProfesorAsociadoActivo(id, activo) {
    return apiService.put(`/particulares/profesores-asociados/${id}/activo`, { activo });
  }

  resetProfesorAsociadoPassword(id) {
    return apiService.put(`/particulares/profesores-asociados/${id}/reset-password`);
  }

  getAlumnos() {
    return apiService.get('/particulares/alumnos');
  }

  createAlumno(payload) {
    return apiService.post('/particulares/alumnos', payload);
  }

  updateAlumno(id, payload) {
    return apiService.put(`/particulares/alumnos/${id}`, payload);
  }

  updateAlumnoActivo(id, activo) {
    return apiService.put(`/particulares/alumnos/${id}/activo`, { activo });
  }

  resetAlumnoPassword(id) {
    return apiService.put(`/particulares/alumnos/${id}/reset-password`);
  }

  getAlumnoInvitaciones() {
    return apiService.get('/particulares/alumnos/invitaciones');
  }

  getAlumnoInvitacion(id) {
    return apiService.get(`/particulares/alumnos/invitaciones/${encodeURIComponent(id)}`);
  }

  createAlumnoInvitacion(payload) {
    return apiService.post('/particulares/alumnos/invitaciones', payload);
  }

  updateAlumnoInvitacion(id, payload) {
    return apiService.put(`/particulares/alumnos/invitaciones/${encodeURIComponent(id)}`, payload);
  }

  disableAlumnoInvitacion(id) {
    return apiService.put(`/particulares/alumnos/invitaciones/${encodeURIComponent(id)}/deshabilitar`);
  }

  updateAlumnoInvitacionActivo(id, activo) {
    return apiService.put(`/particulares/alumnos/invitaciones/${encodeURIComponent(id)}/activo`, { activo });
  }

  getAlumnoInvitacionPublica(codigo) {
    return apiService.getPublic(`/particulares/invitaciones-alumnos/${encodeURIComponent(codigo)}`);
  }

  registrarAlumnoPorInvitacion(payload) {
    return apiService.postPublic('/particulares/invitaciones-alumnos/registrar', payload);
  }

  getCuentaCorriente(filters = {}) {
    const params = new URLSearchParams();
    if (filters.fechaDesde) params.append('fechaDesde', filters.fechaDesde);
    if (filters.fechaHasta) params.append('fechaHasta', filters.fechaHasta);
    if (filters.tipoMovimiento && filters.tipoMovimiento !== 'todos') params.append('tipoMovimiento', filters.tipoMovimiento);
    if (filters.tipoEntidad && filters.tipoEntidad !== 'todos') params.append('tipoEntidad', filters.tipoEntidad);
    if (filters.idUsuarioEntidad) params.append('idUsuarioEntidad', filters.idUsuarioEntidad);
    if (filters.search) params.append('search', filters.search);
    if (Number.isFinite(filters.skip)) params.append('skip', filters.skip);
    if (Number.isFinite(filters.take)) params.append('take', filters.take);
    const query = params.toString();
    return apiService.get(`/particulares/cuenta-corriente${query ? `?${query}` : ''}`);
  }

  createCuentaCorrienteMovimiento(payload) {
    return apiService.post('/particulares/cuenta-corriente', payload);
  }

  updateCuentaCorrienteMovimiento(id, payload) {
    return apiService.put(`/particulares/cuenta-corriente/${encodeURIComponent(id)}`, payload);
  }

  deleteCuentaCorrienteMovimiento(id) {
    return apiService.delete(`/particulares/cuenta-corriente/${encodeURIComponent(id)}`);
  }

  getCursos() {
    return apiService.get('/particulares/cursos');
  }

  createCurso(payload) {
    return apiService.post('/particulares/cursos', payload);
  }

  updateCurso(id, payload) {
    return apiService.put(`/particulares/cursos/${id}`, payload);
  }

  deleteCurso(id) {
    return apiService.delete(`/particulares/cursos/${encodeURIComponent(id)}`);
  }

  setCursoProfesores(id, ids) {
    return apiService.put(`/particulares/cursos/${id}/profesores`, { ids });
  }

  setCursoAlumnos(id, ids) {
    return apiService.put(`/particulares/cursos/${id}/alumnos`, { ids });
  }

  setCursoEtiquetas(id, ids) {
    return apiService.put(`/particulares/cursos/${id}/etiquetas`, { ids });
  }

  getAulas(cursoId = null) {
    const query = cursoId ? `?cursoId=${encodeURIComponent(cursoId)}` : '';
    return apiService.get(`/particulares/aulas${query}`);
  }

  createAula(payload) {
    return apiService.post('/particulares/aulas', payload);
  }

  updateAula(id, payload) {
    return apiService.put(`/particulares/aulas/${id}`, payload);
  }

  deleteAula(id) {
    return apiService.delete(`/particulares/aulas/${id}`);
  }

  getAulaStats(id, options = {}) {
    if (!id) throw new Error('No se encontró el aula.');
    const params = new URLSearchParams();
    if (options.from) params.append('from', options.from);
    if (options.to) params.append('to', options.to);
    const query = params.toString();
    return apiService.get(`/particulares/aulas/${encodeURIComponent(id)}/estadisticas${query ? `?${query}` : ''}`);
  }

  createAulaTelemetry(id, payload = {}) {
    if (!id) throw new Error('No se encontró el aula.');
    return apiService.post(`/particulares/aulas/${encodeURIComponent(id)}/telemetria`, payload);
  }

  getAsistentes() {
    return apiService.get('/particulares/asistentes');
  }

  getAsistenteById(idOpenAIAssistant) {
    if (!idOpenAIAssistant) throw new Error('Asistente inválido.');
    return apiService.get(`/particulares/asistentes/${encodeURIComponent(idOpenAIAssistant)}`);
  }

  createSolicitudGptClasico(formData) {
    return apiService.request('/particulares/solicitudes-gpt-clasicos', {
      method: 'POST',
      body: formData
    });
  }

  getMisSolicitudesGptClasicos({ skip = 0, take = 20 } = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(skip)) params.append('skip', skip);
    if (Number.isFinite(take)) params.append('take', take);
    const query = params.toString();
    return apiService.get(`/particulares/mis-solicitudes-gpt-clasicos${query ? `?${query}` : ''}`);
  }

  getSolicitudesRecibidasGptClasicos({ skip = 0, take = 20, search = '', leido = 'todos', tipo = 'todos' } = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(skip)) params.append('skip', skip);
    if (Number.isFinite(take)) params.append('take', take);
    if (search) params.append('search', search);
    if (leido) params.append('leido', leido);
    if (tipo) params.append('tipo', tipo);
    const query = params.toString();
    return apiService.get(`/particulares/solicitudes-gpt-clasicos/recibidas${query ? `?${query}` : ''}`);
  }

  getSolicitudesRecibidasAcciones(id) {
    if (!id) throw new Error('Solicitud inválida.');
    return apiService.get(`/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}/acciones`);
  }

  async downloadSolicitudArchivoAgregar(id, fileIndex) {
    if (!id) throw new Error('Solicitud inválida.');
    if (!Number.isInteger(fileIndex) || fileIndex < 0) throw new Error('Archivo inválido.');
    const url = `${apiService.baseURL}/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}/archivos-agregar/${fileIndex}/download`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(await getServerDownloadMessage(response, 'No pudimos descargar el archivo.'));
    return response.blob();
  }

  async downloadSolicitudArchivosAgregarZip(id) {
    if (!id) throw new Error('Solicitud inválida.');
    const url = `${apiService.baseURL}/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}/archivos-agregar/zip`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(await getServerDownloadMessage(response, 'No pudimos descargar el ZIP.'));
    return response.blob();
  }

  updateSolicitudLeido(id, leido) {
    if (!id) throw new Error('Solicitud inválida.');
    return apiService.put(`/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}/leido`, {
      leido: Boolean(leido)
    });
  }

  deleteSolicitudRecibida(id) {
    if (!id) throw new Error('Solicitud inválida.');
    return apiService.delete(`/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}`);
  }

  finalizarSolicitud(id, payload) {
    if (!id) throw new Error('Solicitud inválida.');
    return apiService.put(`/particulares/solicitudes-gpt-clasicos/recibidas/${encodeURIComponent(id)}/finalizar`, payload);
  }

  getAulaAsistentes(id) {
    return apiService.get(`/particulares/aulas/${id}/asistentes`);
  }

  setAulaAsistentes(id, ids) {
    return apiService.put(`/particulares/aulas/${id}/asistentes`, { ids });
  }

  getAulaAsistentesIA(id) {
    return apiService.get(`/particulares/aulas/${encodeURIComponent(id)}/asistentes-ia`);
  }

  addAulaAsistenteIA(id, assistantId) {
    return apiService.post(`/particulares/aulas/${encodeURIComponent(id)}/asistentes-ia`, {
      idOpenAIAssistant: assistantId
    });
  }

  removeAulaAsistenteIA(id, assistantId) {
    return apiService.delete(`/particulares/aulas/${encodeURIComponent(id)}/asistentes-ia/${encodeURIComponent(assistantId)}`);
  }

  updateAulaRecursosVisibilidad(id, payload) {
    return apiService.put(`/particulares/aulas/${encodeURIComponent(id)}/recursos-visibilidad`, payload);
  }

  getAulaMensajes(id, options = {}) {
    const params = new URLSearchParams();
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.search) params.append('search', options.search);
    if (options.scope) params.append('scope', options.scope);
    const query = params.toString();
    return apiService.get(`/particulares/aulas/${encodeURIComponent(id)}/mensajes${query ? `?${query}` : ''}`);
  }

  createAulaMensaje(id, payload) {
    return apiService.post(`/particulares/aulas/${encodeURIComponent(id)}/mensajes`, payload);
  }

  updateAulaMensaje(id, mensajeId, payload) {
    return apiService.put(`/particulares/aulas/${encodeURIComponent(id)}/mensajes/${encodeURIComponent(mensajeId)}`, payload);
  }

  deleteAulaMensaje(id, mensajeId) {
    return apiService.delete(`/particulares/aulas/${encodeURIComponent(id)}/mensajes/${encodeURIComponent(mensajeId)}`);
  }

  getAulaLinks(id) {
    return apiService.get(`/particulares/aulas/${encodeURIComponent(id)}/links`);
  }

  createAulaLink(id, payload) {
    return apiService.post(`/particulares/aulas/${encodeURIComponent(id)}/links`, {
      url: payload?.url || '',
      descripcion: payload?.descripcion || ''
    });
  }

  updateAulaLink(id, linkId, payload) {
    return apiService.put(`/particulares/aulas/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`, {
      url: payload?.url || '',
      descripcion: payload?.descripcion || '',
      activo: payload?.activo !== false
    });
  }

  deleteAulaLink(id, linkId) {
    return apiService.delete(`/particulares/aulas/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`);
  }

  getAulaArchivos(id) {
    return apiService.get(`/particulares/aulas/${encodeURIComponent(id)}/archivos`);
  }

  uploadAulaArchivo(id, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiService.request(`/particulares/aulas/${encodeURIComponent(id)}/archivos`, {
      method: 'POST',
      body: formData
    });
  }

  deleteAulaArchivo(id, archivoId) {
    return apiService.delete(`/particulares/aulas/${encodeURIComponent(id)}/archivos/${encodeURIComponent(archivoId)}`);
  }

  async downloadAulaArchivoZip(id) {
    const url = `${apiService.baseURL}/particulares/aulas/${encodeURIComponent(id)}/archivos/zip`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(await getServerDownloadMessage(response, 'No pudimos descargar el ZIP.'));
    return response.blob();
  }

  async downloadAulaArchivo(id, archivoId) {
    const url = `${apiService.baseURL}/particulares/aulas/${encodeURIComponent(id)}/archivos/${encodeURIComponent(archivoId)}`;
    const headers = apiService.getAuthHeaders();
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(await getServerDownloadMessage(response, 'No pudimos descargar el archivo.'));
    return response.blob();
  }

  getAlumnoPanel() {
    return apiService.get('/particulares/alumno-panel');
  }

  getAlumnoCalendario({ from = '', to = '' } = {}) {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const query = params.toString();
    return apiService.get(`/particulares/alumno-calendario${query ? `?${query}` : ''}`);
  }

  reservarAlumnoDisponibilidad(id, payload) {
    return apiService.post(`/particulares/alumno-calendario/${encodeURIComponent(id)}/reservar`, payload);
  }
}

const particularesApiService = new ParticularesApiService();

export default particularesApiService;
